import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { iniciativasAPI, personasAPI } from '../services/api';

const Summary = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('projects'); // 'projects' or 'people'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the new optimized endpoint that fetches all iniciativas with their personas in one request
      const response = await iniciativasAPI.getAllWithPersonas();
      console.log('Optimized response:', response.data); // Debug log
      
      let projectsData;
      if (response.data.results) {
        // New optimized endpoint response
        projectsData = response.data.results;
      } else if (Array.isArray(response.data)) {
        // Direct array response
        projectsData = response.data;
      } else {
        console.error('Unexpected response structure:', response.data);
        setError('Error: Los datos de proyectos no tienen el formato esperado');
        return;
      }
      
      // Ensure projectsData is an array
      if (!Array.isArray(projectsData)) {
        console.error('Expected array but got:', typeof projectsData, projectsData);
        setError('Error: Los datos de proyectos no tienen el formato esperado');
        return;
      }
      
      // The new endpoint already includes the personas data, so no need for additional requests
      setProjects(projectsData);
    } catch (err) {
      setError('Error al cargar los datos del resumen');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const filteredProjects = projects.filter(project => {
    const searchLower = searchTerm.toLowerCase();
    const projectMatch = project.nombre_de_la_oportunidad.toLowerCase().includes(searchLower) ||
                        project.negocio.toLowerCase().includes(searchLower) ||
                        (project.tecnologia_nombre && project.tecnologia_nombre.toLowerCase().includes(searchLower)) ||
                        (project.area_ejecucion_nombre && project.area_ejecucion_nombre.toLowerCase().includes(searchLower)) ||
                        (project.estado_display && project.estado_display.toLowerCase().includes(searchLower));
    
    const peopleMatch = project.people.some(person => 
      person.persona_nombre.toLowerCase().includes(searchLower)
    );
    
    const statusMatch = !statusFilter || project.estado === statusFilter;
    
    return (projectMatch || peopleMatch) && statusMatch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    return new Date(dateString).toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const getPeopleByRole = (people) => {
    return people.reduce((acc, person) => {
      if (!acc[person.rol]) acc[person.rol] = [];
      acc[person.rol].push(person);
      return acc;
    }, {});
  };

  if (loading) {
    return <div className="loading">Cargando resumen...</div>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Resumen de Proyectos y Equipos</h1>
          <p className="text-muted">Vista compacta de todos los proyectos y sus equipos asignados</p>
        </div>
        <button 
          className="btn btn-secondary"
          onClick={() => {
            if (expandedItems.size === projects.length) {
              setExpandedItems(new Set());
            } else {
              setExpandedItems(new Set(projects.map(p => p.id)));
            }
          }}
        >
          {expandedItems.size === projects.length ? 'Contraer Todo' : 'Expandir Todo'}
        </button>
      </div>

      {/* Search and Filter */}
      <div className="row mb-4">
        <div className="col-md-4">
          <input
            type="text"
            placeholder="Buscar por proyecto, negocio, tecnología o persona..."
            className="form-control"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <select
            className="form-control"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Todos los estados</option>
            <option value="oportunidad_identificada">Oportunidad identificada</option>
            <option value="en_preparacion_propuesta">En preparación propuesta</option>
            <option value="supervision">Supervisión</option>
            <option value="entregada">Entregada</option>
            <option value="rechazada">Rechazada</option>
            <option value="stand_by">Stand-By</option>
            <option value="aprobado">Aprobado</option>
            <option value="planificada">Planificada</option>
            <option value="en_curso">En curso</option>
            <option value="bloqueada">Bloqueada</option>
            <option value="cancelada">Cancelada</option>
            <option value="finalizada">Finalizada</option>
          </select>
        </div>
        <div className="col-md-5">
          <div className="d-flex align-items-center">
            <span className="text-muted mr-2">Resultados: </span>
            <strong>{filteredProjects.length}</strong>
            <span className="text-muted ml-1">de {projects.length} proyectos</span>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-center">
            <h3 style={{ color: '#dc3545', margin: 0 }}>{projects.length}</h3>
            <small className="text-muted">Total Proyectos</small>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <h3 style={{ color: '#666666', margin: 0 }}>
              {projects.reduce((sum, p) => sum + p.people.filter(person => person.activo).length, 0)}
            </h3>
            <small className="text-muted">Personas Activas</small>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <h3 style={{ color: '#dc3545', margin: 0 }}>
              {projects.filter(p => p.people.length === 0).length}
            </h3>
            <small className="text-muted">Sin Equipo</small>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <h3 style={{ color: '#dc3545', margin: 0 }}>
              {Math.round(projects.reduce((sum, p) => sum + p.people.length, 0) / Math.max(projects.length, 1) * 10) / 10}
            </h3>
            <small className="text-muted">Promedio Personas/Proyecto</small>
          </div>
        </div>
      </div>

      {/* Projects List */}
      {filteredProjects.length === 0 ? (
        <div className="alert alert-info">
          {searchTerm ? 'No se encontraron proyectos que coincidan con la búsqueda.' : 'No hay proyectos disponibles.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredProjects.map(project => {
            const isExpanded = expandedItems.has(project.id);
            const activePeople = project.people.filter(person => person.activo);
            const peopleByRole = getPeopleByRole(activePeople);
            
            return (
              <div key={project.id} className="card summary-project-card">
                {/* Project Header */}
                <div 
                  className="card-header expandable-header" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleExpanded(project.id)}
                >
                  <div className="d-flex justify-content-between align-items-center summary-project-header">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center">
                        <span style={{ marginRight: '8px', fontSize: '14px' }}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        <div>
                          <h4 style={{ margin: 0, color: '#333' }}>
                            <Link 
                              to={`/projects/${project.id}`}
                              style={{ textDecoration: 'none', color: 'inherit' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {project.nombre_de_la_oportunidad}
                            </Link>
                          </h4>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {project.negocio} • {project.subnegocio}
                            {project.tecnologia_nombre && ` • ${project.tecnologia_nombre}`}
                            {project.area_ejecucion_nombre && ` • Ejecución: ${project.area_ejecucion_nombre}`}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="d-flex align-items-center summary-stats">
                      {/* Team Count and Names */}
                      <div className="mr-3" style={{ maxWidth: '300px' }}>
                        <div className="d-flex align-items-center mb-1">
                          <div style={{ 
                            backgroundColor: activePeople.length > 0 ? '#dc3545' : '#666666',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            marginRight: '8px'
                          }}>
                            {activePeople.length} persona{activePeople.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        {activePeople.length > 0 && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#666',
                            lineHeight: 1.2,
                            maxHeight: '32px',
                            overflow: 'hidden'
                          }}>
                            {activePeople.slice(0, 3).map(person => person.persona_nombre).join(', ')}
                            {activePeople.length > 3 && ` +${activePeople.length - 3} más`}
                          </div>
                        )}
                      </div>
                      
                      {/* Date */}
                      <div className="text-center mr-3">
                        <div style={{ fontSize: '12px', color: '#666' }}>Entrega</div>
                        <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
                          {formatDate(project.fecha_de_entrega_propuesta_prevista)}
                        </div>
                      </div>
                      
                      {/* Quick Actions */}
                      <Link 
                        to={`/projects/${project.id}`}
                        className="btn btn-sm btn-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Ver Detalles
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="card-body">
                    <div className="row">
                      {/* Project Info */}
                      <div className="col-md-4">
                        <h6 style={{ marginBottom: '10px', color: '#dc3545' }}>Información del Proyecto</h6>
                        {project.descripcion && (
                          <div className="mb-2">
                            <small className="text-muted">Descripción:</small>
                            <div style={{ fontSize: '13px' }}>
                              {project.descripcion.length > 150 
                                ? project.descripcion.substring(0, 150) + '...' 
                                : project.descripcion}
                            </div>
                          </div>
                        )}
                        
                        <div className="row">
                          <div className="col-6">
                            <small className="text-muted">Resp. Comercial:</small>
                            <div style={{ fontSize: '13px' }}>
                              {project.responsable_comercial_nombre || 'No asignado'}
                            </div>
                          </div>
                          <div className="col-6">
                            <small className="text-muted">Líder Técnico:</small>
                            <div style={{ fontSize: '13px' }}>
                              {project.lider_tecnico_nombre || 'No asignado'}
                            </div>
                          </div>
                        </div>
                        
                        {/* Planning dates section */}
                        <div className="mt-3">
                          <small className="text-muted">Fechas de Planificación:</small>
                          <div className="row" style={{ fontSize: '12px', marginTop: '4px' }}>
                            <div className="col-6">
                              <strong>Inicio:</strong> {formatDate(project.fecha_de_inicio_planificada) || 'No definido'}
                              {project.fecha_de_inicio_real && (
                                <div style={{ color: '#28a745' }}>Real: {formatDate(project.fecha_de_inicio_real)}</div>
                              )}
                            </div>
                            <div className="col-6">
                              <strong>Fin:</strong> {formatDate(project.fecha_de_fin_planificada) || 'No definido'}
                              {project.fecha_de_fin_real && (
                                <div style={{ color: '#28a745' }}>Real: {formatDate(project.fecha_de_fin_real)}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Team Composition */}
                      <div className="col-md-8">
                        <h6 style={{ marginBottom: '10px', color: '#666666' }}>
                          Equipo Asignado ({activePeople.length} personas activas)
                        </h6>
                        
                        {activePeople.length === 0 ? (
                          <div className="alert alert-info" style={{ padding: '8px 12px', margin: 0 }}>
                            No hay personas asignadas a este proyecto
                          </div>
                        ) : (
                          <div>
                            {Object.entries(peopleByRole).map(([role, people]) => (
                              <div key={role} className="mb-2">
                                <div style={{ 
                                  fontSize: '12px', 
                                  fontWeight: 'bold',
                                  color: '#495057',
                                  marginBottom: '4px'
                                }}>
                                  {people[0].rol_display} ({people.length})
                                </div>
                                <div className="d-flex flex-wrap">
                                  {people.map(person => (
                                    <Link
                                      key={person.id}
                                      to={`/people/${person.id}`}
                                      className="summary-team-member"
                                      style={{
                                        textDecoration: 'none',
                                        backgroundColor: '#f8f9fa',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        marginRight: '4px',
                                        marginBottom: '4px',
                                        border: '1px solid #dee2e6',
                                        color: '#495057',
                                        display: 'inline-block'
                                      }}
                                    >
                                      {person.persona_nombre}
                                      {person.porcentaje_dedicacion && (
                                        <span style={{ marginLeft: '4px', opacity: 0.7 }}>
                                          ({person.porcentaje_dedicacion}%)
                                        </span>
                                      )}
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer Summary */}
      <div className="card mt-4">
        <div className="card-header">
          <h4 className="card-title">Resumen de Roles</h4>
        </div>
        <div className="card-body">
          {(() => {
            const allRoles = {};
            projects.forEach(project => {
              project.people.filter(p => p.activo).forEach(person => {
                if (!allRoles[person.rol]) {
                  allRoles[person.rol] = { count: 0, display: person.rol_display };
                }
                allRoles[person.rol].count++;
              });
            });

            return Object.entries(allRoles).length > 0 ? (
              <div className="row">
                {Object.entries(allRoles)
                  .sort(([,a], [,b]) => b.count - a.count)
                  .map(([role, data]) => (
                    <div key={role} className="col-md-3 mb-3 text-center">
                      <div
                        style={{
                          backgroundColor: '#f8f9fa',
                          color: '#495057',
                          border: '1px solid #dee2e6',
                          padding: '10px',
                          borderRadius: '8px'
                        }}
                      >
                        <h5 style={{ margin: 0 }}>{data.count}</h5>
                        <small>{data.display}</small>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-muted">No hay datos de roles disponibles</p>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default Summary;